// ═══════════════════════════════════════════════════════════════════════════
// AI-callable safe-contour guard — MATCHPROP domain (CC-2c PR-1)
// ═══════════════════════════════════════════════════════════════════════════
//
// Mirrors the draw-modify `assertAiCallableDrawAction` (drawModifyGuard.ts): maps
// every MATCHPROP AI action to the user-facing registry command it targets, then
// reuses the CC-2a generic `assertAiCallableCommand` to fail CLOSED when that
// target is not `aiCallable: true` (or is absent from the registry).
//
// The guard is ACTION-level, not literal-level: the registry descriptor the
// action targets is checked (MATCH_PROPERTIES → "MATCHPROP"), never the internal
// EditorCommand literals the translator emits (`matchprop/set-source`,
// `matchprop/apply`). This is the same rule the block / entity / draw / view-space
// guards document — the AI-callable surface is the COMMAND the AI is asking for,
// while the emitted matchprop literals are internal mechanics of the safe
// contour (the MATCHPROP descriptor's two `commandTypeLiterals` are plumbing).

import {
  ALL_COMMAND_DESCRIPTORS,
  type CommandDescriptor,
  type CommandRegistryId,
} from "@dxf-ai-editor/command-registry";

import { assertAiCallableCommand } from "./aiCallableGuard";
import type { MatchpropAiCommand } from "./matchpropSchemas";

/**
 * Maps every MATCHPROP AI action to the registry command it targets. The guard
 * resolves this id and checks its `aiCallable` flag, so an action whose target
 * is ever flipped to `aiCallable: false` fails closed automatically.
 *
 * `Record<MatchpropAiCommand["action"], …>` forces exhaustiveness: adding a new
 * MATCHPROP-family action without declaring its target command is a compile
 * error. The target is `aiCallable: true` in the registry today (system.ts) and
 * listed AI-callable in ТЗ §10.1.
 *
 * Mapping rationale (see command-registry/commands/system.ts):
 *   • MATCH_PROPERTIES → MATCHPROP — copies a source entity's properties onto a
 *     batch of targets; the `matchprop/set-source` + `matchprop/apply` literals
 *     it dispatches are internal plumbing (the descriptor's commandTypeLiterals).
 */
export const MATCHPROP_AI_ACTION_TARGET_COMMAND: Record<
  MatchpropAiCommand["action"],
  CommandRegistryId
> = {
  MATCH_PROPERTIES: "MATCHPROP",
};

/**
 * Guard a single MATCHPROP AI action: resolves its target registry command and
 * asserts AI-callability. Throws `NonAiCallableCommandError` when the target is
 * not AI-callable (fail-closed). Called by `translateMatchpropCommand` before
 * any EditorCommand is produced.
 */
export function assertAiCallableMatchpropAction(
  action: MatchpropAiCommand["action"],
  descriptors: readonly CommandDescriptor[] = ALL_COMMAND_DESCRIPTORS,
): void {
  assertAiCallableCommand(
    MATCHPROP_AI_ACTION_TARGET_COMMAND[action],
    descriptors,
  );
}
